import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

const SEO = ({ 
  title, 
  description, 
  keywords, 
  image, 
  url, 
  type = 'website',
  author = 'Scrum Master & Agile Coach',
  schema,
  hreflang
}) => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const siteTitle = 'Professional Scrum Master & Agile Coach';
  const fullTitle = title ? `${title} | ${siteTitle}` : siteTitle;
  const defaultDescription = 'Transform your team\'s productivity with expert guidance in Agile methodologies, Scrum frameworks, and organizational change.';
  const defaultKeywords = 'Scrum Master, Agile Coach, Agile Transformation, Scrum Training, Team Coaching, Digital Transformation, Product Management';
  const defaultImage = 'https://scrumnx.com/og-image.jpg';
  const baseUrl = 'https://scrumnx.com';
  
  // Generate canonical URL - use provided url or construct from location
  const canonicalUrl = url || `${baseUrl}${location.pathname}`;
  
  // Generate full image URL
  const fullImageUrl = image ? (image.startsWith('http') ? image : `${baseUrl}${image}`) : defaultImage;
  
  // Determine locale for OG tags
  const ogLocale = i18n.language === 'de' ? 'de_DE' : 'en_US';
  
  // Generate hreflang URLs if not provided
  const currentPath = location.pathname;
  const hreflangUrls = hreflang || {
    en: `${baseUrl}${currentPath}`,
    de: `${baseUrl}${currentPath}`,
    'x-default': `${baseUrl}${currentPath}`
  };

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description || defaultDescription} />
      <meta name="keywords" content={keywords || defaultKeywords} />
      <meta name="author" content={author} />
      <meta name="robots" content="index, follow" />
      <meta name="language" content={i18n.language === 'de' ? 'German' : 'English'} />
      <meta name="revisit-after" content="7 days" />
      <meta name="distribution" content="global" />
      <meta name="rating" content="general" />
      <meta name="googlebot" content="index, follow" />
      <meta name="bingbot" content="index, follow" />

      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Hreflang Tags for i18n */}
      <link rel="alternate" hreflang="en" href={hreflangUrls.en} />
      <link rel="alternate" hreflang="de" href={hreflangUrls.de} />
      <link rel="alternate" hreflang="x-default" href={hreflangUrls['x-default']} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description || defaultDescription} />
      <meta property="og:image" content={fullImageUrl} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content={siteTitle} />
      <meta property="og:locale" content={ogLocale} />
      {i18n.language === 'de' && <meta property="og:locale:alternate" content="en_US" />}
      {i18n.language === 'en' && <meta property="og:locale:alternate" content="de_DE" />}

      {/* Twitter - Removed placeholder, only include if Twitter handle is available */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:title" content={fullTitle} />
      <meta property="twitter:description" content={description || defaultDescription} />
      <meta property="twitter:image" content={fullImageUrl} />
      {/* Twitter handle removed - add back when actual handle is available */}
      {/* <meta property="twitter:site" content="@scrumnx" /> */}
      {/* <meta property="twitter:creator" content="@scrumnx" /> */}

      {/* Additional SEO Meta Tags */}
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <meta name="theme-color" content="#1f251c" />
      <meta name="msapplication-TileColor" content="#1f251c" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content={siteTitle} />

      {/* Structured Data (JSON-LD) */}
      {schema && (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO; 